/**
 * Debug Console Tools
 * Expone imbasion.debug en la consola del navegador
 */

import Phaser from 'phaser';
import { storage } from './Storage';
import { DebugManagerHotReload } from '../debug/DebugManagerHotReload';

interface ImbasionDebug {
  clear: (reload?: boolean) => void;
  clearCache: (reload?: boolean) => Promise<void>;
  clearWeapons: () => void;
  clearProgress: () => void;
  reloadData: () => Promise<void>;
  info: () => void;
}

interface ImbasionGlobal {
  debug: ImbasionDebug;
  storage: typeof storage;
  game?: Phaser.Game;
}

const imbasionDebug: ImbasionDebug = {
  clear: (reload = false) => {
    storage.resetAll();
    console.log('%c[Imbasion] All localStorage cleared!', 'color: #ff6b6b; font-weight: bold');
    if (reload) {
      console.log('%c[Imbasion] Reloading...', 'color: #ffa502');
      window.location.reload();
    } else {
      console.log('%c[Imbasion] Run imbasion.debug.clear(true) to also reload.', 'color: #888');
    }
  },
  clearCache: async (reload = false) => {
    // Clear localStorage
    storage.resetAll();
    console.log('%c[Imbasion] localStorage cleared', 'color: #ff6b6b');

    // Clear browser caches (fetch/HTTP cache)
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      for (const name of cacheNames) {
        await caches.delete(name);
        console.log(`%c[Imbasion] Cache "${name}" deleted`, 'color: #ff6b6b');
      }
    }

    // Clear sessionStorage
    sessionStorage.clear();
    console.log('%c[Imbasion] sessionStorage cleared', 'color: #ff6b6b');

    console.log('%c[Imbasion] All caches cleared!', 'color: #1dd1a1; font-weight: bold');

    if (reload) {
      console.log('%c[Imbasion] Hard reloading...', 'color: #ffa502');
      // Force hard reload (bypass cache)
      window.location.href = window.location.href.split('?')[0] + '?nocache=' + Date.now();
    } else {
      console.log('%c[Imbasion] Run imbasion.debug.clearCache(true) to also reload.', 'color: #888');
    }
  },
  clearWeapons: () => {
    storage.resetWeaponState();
    console.log('%c[Imbasion] Weapon state cleared! Reload the page.', 'color: #ffa502; font-weight: bold');
  },
  clearProgress: () => {
    localStorage.removeItem('imbasion_score');
    localStorage.removeItem('imbasion_highscore');
    localStorage.removeItem('imbasion_cities');
    localStorage.removeItem('imbasion_level');
    localStorage.removeItem('imbasion_unlocked_levels');
    console.log('%c[Imbasion] Progress cleared! Reload the page.', 'color: #ffa502; font-weight: bold');
  },
  reloadData: async () => {
    const imbasion = (window as unknown as { imbasion: ImbasionGlobal }).imbasion;
    const game = imbasion?.game;
    if (!game) {
      console.error('[Imbasion] Game instance not available. Make sure you are in a game scene.');
      return;
    }
    // Get the active Game scene
    const scene = game.scene.getScene('Game');
    if (!scene || !scene.scene.isActive()) {
      console.error('[Imbasion] Game scene not active. Enter a level first.');
      return;
    }
    const success = await DebugManagerHotReload.reloadOverrides(scene);
    DebugManagerHotReload.showNotification(scene, success);
  },
  info: () => {
    console.log('%c[Imbasion Debug Commands]', 'color: #1dd1a1; font-weight: bold');
    console.log('  imbasion.debug.clear(reload?)      - Clear localStorage (reload=true to refresh)');
    console.log('  imbasion.debug.clearCache(reload?) - Clear ALL caches + localStorage (reload=true for hard refresh)');
    console.log('  imbasion.debug.clearWeapons()      - Clear weapon state only');
    console.log('  imbasion.debug.clearProgress()     - Clear score/levels only');
    console.log('  imbasion.debug.reloadData()        - Hot-reload override JSONs (or SHIFT+F5)');
    console.log('  imbasion.debug.info()              - Show this help');
    console.log('%c[Debug Hotkeys]', 'color: #1dd1a1; font-weight: bold');
    console.log('  SHIFT+F1 - Toggle collision debug overlay');
    console.log('  SHIFT+F2 - Toggle range debug overlay');
    console.log('  SHIFT+F3 - Toggle object inspector');
    console.log('  SHIFT+F4 - Toggle particle debug');
    console.log('  SHIFT+F5 - Hot-reload override JSONs');
    console.log('  SHIFT+F6 - Toggle image points overlay');
  }
};

export function initDebugTools(): void {
  if (typeof window !== 'undefined') {
    (window as unknown as { imbasion: ImbasionGlobal }).imbasion = {
      debug: imbasionDebug,
      storage: storage
    };
  }
}

/**
 * Set the Phaser game instance for debug tools.
 * Call this after game creation to enable hot-reload.
 */
export function setDebugGameInstance(game: Phaser.Game): void {
  if (typeof window !== 'undefined') {
    const imbasion = (window as unknown as { imbasion: ImbasionGlobal }).imbasion;
    if (imbasion) {
      imbasion.game = game;
    }
  }
}
